import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useReveal } from "@/hooks/useReveal";
import ListingsCarousel, { CarouselListing } from "./ListingsCarousel";
import NearYouCarousel from "./NearYouCarousel";

const SELECT = "id, title, brand, model, price, city, images, condition, dgt_approved, created_at";

export type CarouselKey = "recent" | "best_price" | "most_viewed" | "dgt" | "near_you";

export type CarouselConfig = {
  key: CarouselKey;
  enabled: boolean;
};

export const DEFAULT_CAROUSELS: CarouselConfig[] = [
  { key: "recent", enabled: true },
  { key: "best_price", enabled: true },
  { key: "most_viewed", enabled: true },
  { key: "dgt", enabled: true },
  { key: "near_you", enabled: true },
];

const LABELS: Record<Exclude<CarouselKey, "near_you">, { title: string; subtitle: string; emptyHint: string; viewAllHref?: string }> = {
  recent: { title: "Los más recientes", subtitle: "Últimos patinetes publicados en el marketplace", emptyHint: "Aún no hay anuncios publicados. ¡Sé el primero!" },
  best_price: { title: "Mejor precio", subtitle: "Las mejores oportunidades del momento", emptyHint: "Sin ofertas disponibles por ahora.", viewAllHref: "/buscar?sort=price_asc" },
  most_viewed: { title: "Más vistos", subtitle: "Lo que está arrasando entre los compradores", emptyHint: "Aún no hay datos de visualizaciones." },
  dgt: { title: "Homologados DGT", subtitle: "Patinetes aptos para circular legalmente", emptyHint: "Aún no hay patinetes homologados publicados.", viewAllHref: "/buscar?dgt=1" },
};

const HomeCarousels = () => {
  const { user } = useAuth();
  // Defer all fetches until the section approaches the viewport, removing
  // ~8 non-critical requests from the initial network dependency tree.
  const { ref: gateRef, visible } = useReveal<HTMLDivElement>({
    threshold: 0,
    rootMargin: "400px 0px",
  });
  const [data, setData] = useState<Record<Exclude<CarouselKey, "near_you">, CarouselListing[]>>({
    recent: [], best_price: [], most_viewed: [], dgt: [],
  });
  const [loading, setLoading] = useState(true);
  const [monthlyRate, setMonthlyRate] = useState(0.04);
  const [carousels, setCarousels] = useState<CarouselConfig[]>(DEFAULT_CAROUSELS);
  const [autoplay, setAutoplay] = useState(false);
  const [defaultRadius, setDefaultRadius] = useState(50);

  useEffect(() => {
    if (!visible) return;
    (async () => {
      // Batch the 4 site_settings keys into a single request (was 4 round-trips).
      const settingsKeys = [
        "monthly_finance_rate",
        "home_carousels",
        "home_carousels_autoplay",
        "near_you_radius_km",
      ];
      const [r, bp, mv, d, settingsRes] = await Promise.all([
        supabase.from("listings").select(SELECT).eq("status", "active").order("created_at", { ascending: false }).limit(12),
        supabase.from("listings").select(SELECT).eq("status", "active").order("price", { ascending: true }).limit(12),
        supabase.from("listings").select(SELECT).eq("status", "active").order("views_count", { ascending: false, nullsFirst: false }).limit(12),
        supabase.from("listings").select(SELECT).eq("status", "active").eq("dgt_approved", true).order("created_at", { ascending: false }).limit(12),
        supabase.from("site_settings").select("key, value").in("key", settingsKeys),
      ]);

      setData({
        recent: (r.data as CarouselListing[]) || [],
        best_price: (bp.data as CarouselListing[]) || [],
        most_viewed: (mv.data as CarouselListing[]) || [],
        dgt: (d.data as CarouselListing[]) || [],
      });

      const settingsMap = new Map<string, any>(
        ((settingsRes.data as Array<{ key: string; value: any }> | null) ?? []).map(
          (row) => [row.key, row.value]
        )
      );
      const rate = { data: { value: settingsMap.get("monthly_finance_rate") } };
      const cfg = { data: { value: settingsMap.get("home_carousels") } };
      const ap = { data: { value: settingsMap.get("home_carousels_autoplay") } };
      const radiusCfg = { data: { value: settingsMap.get("near_you_radius_km") } };

      const v = (rate.data as any)?.value;
      const rateNum = typeof v === "number" ? v : typeof v?.rate === "number" ? v.rate : null;
      if (rateNum && rateNum > 0 && rateNum < 1) setMonthlyRate(rateNum);

      const cfgVal = (cfg.data as any)?.value;
      if (Array.isArray(cfgVal) && cfgVal.length > 0) {
        const existing = new Set(cfgVal.map((c: any) => c.key));
        const merged = [
          ...cfgVal.filter((c: any) => DEFAULT_CAROUSELS.some((d) => d.key === c.key)),
          ...DEFAULT_CAROUSELS.filter((d) => !existing.has(d.key)),
        ];
        setCarousels(merged as CarouselConfig[]);
      }

      const apVal = (ap.data as any)?.value;
      setAutoplay(apVal === true || apVal?.enabled === true);

      const rv = (radiusCfg.data as any)?.value;
      if (typeof rv === "number" && rv > 0) setDefaultRadius(rv);

      setLoading(false);
    })();
  }, [visible]);

  return (
    <div ref={gateRef}>
      {carousels.filter((c) => c.enabled).map((c) => {
        if (c.key === "near_you") {
          if (!user) return null;
          return (
            <NearYouCarousel key="near_you" defaultRadiusKm={defaultRadius} monthlyRate={monthlyRate} autoplay={autoplay} />
          );
        }
        const cfg = LABELS[c.key];
        return (
          <ListingsCarousel
            key={c.key}
            title={cfg.title}
            subtitle={cfg.subtitle}
            listings={data[c.key]}
            loading={loading}
            monthlyRate={monthlyRate}
            emptyHint={cfg.emptyHint}
            viewAllHref={cfg.viewAllHref || "/buscar"}
            showViewAll={c.key === "recent"}
            autoplay={autoplay}
          />
        );
      })}
    </div>
  );
};

export default HomeCarousels;
